import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';

import { FormInput, SubsectionForm } from '../../../../components/forms';
import { formFieldInStack } from '../../../../components/forms/formFieldStackContext';
import { Star, Info } from '../../../../components/icons';
import { PermissionAwareField } from '../../../../components/PermissionAwareField';
import { SECTION_IDS } from '../../../../constants/sectionIds';

import type { Request } from '../../../../types';

interface StarRatingProps {
  rating: number;
  onRatingChange: (rating: number) => void;
  disabled?: boolean;
}

const StarRating: React.FC<StarRatingProps> = ({
  rating,
  onRatingChange,
  disabled = false,
}) => {
  const stars = [];

  for (let i = 1; i <= 5; i++) {
    stars.push(
      <TouchableOpacity
        key={i}
        style={feedbackRatingStyles.starButton}
        onPress={() => !disabled && onRatingChange(i)}
        disabled={disabled}
        accessibilityLabel={`Rate ${i} star${i > 1 ? 's' : ''}`}
      >
        <Star
          size={24}
          color={i <= rating ? '#FFD700' : '#E0E0E0'}
          fill={i <= rating ? '#FFD700' : 'transparent'}
        />
      </TouchableOpacity>,
    );
  }

  return <View style={feedbackRatingStyles.starsContainer}>{stars}</View>;
};

export type FeedbackRatingProps =
  | {
      mode: 'initial';
      formData: Record<string, unknown>;
      updateFormData: (field: string, value: string | number) => void;
      disabled: boolean;
      requestId?: string | number | null;
      isOnline?: boolean;
    }
  | {
      mode: 'persisted';
      request: Request;
      isEditing: boolean;
      editedData: Record<string, unknown>;
      updateEditedData: (field: string, value: string | number) => void;
      showFullDetails?: boolean;
      isOnline?: boolean;
    };

const FEEDBACK_COMMENTS_MAX = 1000;

/**
 * Rating + comments card for Feedback (create and opened-request).
 */
export const FeedbackRating: React.FC<FeedbackRatingProps> = props => {
  if (props.mode === 'initial') {
    const {
      formData,
      updateFormData,
      disabled,
      requestId = null,
      isOnline = true,
    } = props;

    const handleRatingSelect = (rating: number) => {
      updateFormData('fb_rating', rating);
    };

    const handleCommentsChange = (comments: string) => {
      updateFormData('fb_comments', comments);
    };

    const renderStars = () => {
      const stars = [];
      const rating = Number(formData.fb_rating ?? 0);

      for (let i = 1; i <= 5; i++) {
        stars.push(
          <TouchableOpacity
            key={i}
            style={feedbackRatingStyles.starButton}
            onPress={() => handleRatingSelect(i)}
            accessibilityLabel={`Rate ${i} star${i > 1 ? 's' : ''}`}
            disabled={disabled}
          >
            <Star
              size={24}
              color={i <= rating ? '#FFD700' : '#E0E0E0'}
              fill={i <= rating ? '#FFD700' : 'transparent'}
            />
          </TouchableOpacity>,
        );
      }

      return stars;
    };

    return (
      <SubsectionForm>
        <View style={feedbackRatingStyles.feedbackContainer}>
          <View style={feedbackRatingStyles.ratingSection}>
            <Text style={feedbackRatingStyles.ratingLabel}>Rating</Text>
            <View style={feedbackRatingStyles.starsContainer}>
              {renderStars()}
            </View>
          </View>

          <View style={feedbackRatingStyles.commentsSection}>
            <View style={feedbackRatingStyles.commentsHeader}>
              <Text style={feedbackRatingStyles.commentsLabel}>Comments</Text>
              <Info size={16} color="#666" />
            </View>
            <FormInput
              testID="form-input-fb_comments"
              label="Comments"
              hideLabel
              value={String(formData.fb_comments ?? '')}
              onChangeText={handleCommentsChange}
              placeholder="Please provide comment"
              multiline
              numberOfLines={6}
              maxLength={FEEDBACK_COMMENTS_MAX}
              disabled={disabled}
              editable={!disabled}
              readOnlyMode={disabled}
              ignorePermissions={disabled}
              richText
              requestId={requestId == null ? undefined : String(requestId)}
              requestSection={SECTION_IDS.FEEDBACK}
              isOnline={isOnline}
              containerStyle={[
                formFieldInStack,
                feedbackRatingStyles.commentsInputContainer,
              ]}
            />
          </View>
        </View>
      </SubsectionForm>
    );
  }

  const {
    request,
    isEditing,
    editedData,
    updateEditedData,
    showFullDetails = true,
    isOnline = true,
  } = props;

  const handleRatingSelect = (rating: number) => {
    if (isEditing) {
      updateEditedData('fb_rating', rating);
    }
  };

  const handleCommentsChange = (comments: string) => {
    if (isEditing) {
      updateEditedData('fb_comments', comments);
    }
  };

  return (
    <SubsectionForm>
      <View style={feedbackRatingStyles.feedbackContainer}>
        <View style={feedbackRatingStyles.ratingSection}>
          <Text style={feedbackRatingStyles.ratingLabel}>Rating</Text>
          <PermissionAwareField
            fieldId="fb_rating"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <StarRating
              rating={
                isEditing
                  ? Number(editedData.fb_rating ?? 0)
                  : Number(request.fb_rating ?? 0)
              }
              onRatingChange={handleRatingSelect}
              disabled={!isEditing}
            />
          </PermissionAwareField>
        </View>

        <View style={feedbackRatingStyles.commentsSection}>
          <View style={feedbackRatingStyles.commentsHeader}>
            <Text style={feedbackRatingStyles.commentsLabel}>Comments</Text>
            <Info size={16} color="#666" />
          </View>
          <PermissionAwareField
            fieldId="fb_comments"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <FormInput
              testID="form-input-fb_comments"
              label="Comments"
              hideLabel
              value={
                isEditing
                  ? String(editedData.fb_comments ?? '')
                  : String(request.fb_comments ?? '')
              }
              onChangeText={handleCommentsChange}
              placeholder={isEditing ? 'Please provide comment' : ''}
              multiline
              numberOfLines={6}
              maxLength={FEEDBACK_COMMENTS_MAX}
              disabled={!isEditing}
              editable={isEditing}
              readOnlyMode={!isEditing}
              ignorePermissions={!isEditing}
              richText
              requestId={request.id}
              requestSection={SECTION_IDS.FEEDBACK}
              isOnline={isOnline}
              containerStyle={[
                formFieldInStack,
                feedbackRatingStyles.commentsInputContainer,
                !isEditing && feedbackRatingStyles.readOnlyInputContainer,
              ]}
            />
          </PermissionAwareField>
        </View>
      </View>
    </SubsectionForm>
  );
};

FeedbackRating.displayName = 'FeedbackRating';

const feedbackRatingStyles = StyleSheet.create({
  feedbackContainer: {
    marginBottom: 16,
  },
  ratingSection: {
    marginBottom: 20,
  },
  ratingLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 12,
  },
  starsContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  starButton: {
    padding: 4,
  },
  commentsSection: {
    marginBottom: 16,
  },
  commentsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 6,
  },
  commentsLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  commentsInputContainer: {
    marginBottom: 0,
  },
  readOnlyInputContainer: {
    opacity: 0.95,
  },
});
